import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Search, Loader2, Cpu, HardDrive, Activity, Server } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { logsApi, adminApi, type LogEntry } from "@/api";
import { useAuth } from "@/hooks/useAuth";

const levelVariant: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  info: "secondary",
  success: "default",
  warning: "outline",
  error: "destructive",
};

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d} 天 ${h} 小時`;
  if (h > 0) return `${h} 小時 ${m} 分`;
  return `${m} 分`;
}

function SystemMonitorTab() {
  const { data: sysInfo, isLoading } = useQuery({
    queryKey: ["system-info"],
    queryFn: async () => {
      const res = await adminApi.systemInfo();
      return res.data!;
    },
    refetchInterval: 5000,
  });

  if (isLoading || !sysInfo) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { host, process: proc, queue } = sysInfo;

  return (
    <div className="space-y-6">
      {/* Host Resources */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">CPU</span>
            </div>
            <div className="text-2xl font-bold">{host.cpu_usage_percent}%</div>
            <p className="text-xs text-muted-foreground mt-1">{host.cpus} 核 {host.cpu_model.split('@')[0].trim()}</p>
            <Progress value={host.cpu_usage_percent} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <HardDrive className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">記憶體</span>
            </div>
            <div className="text-2xl font-bold">{host.memory_usage_percent}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {host.used_memory_mb} MB / {host.total_memory_mb} MB
            </p>
            <Progress value={host.memory_usage_percent} className="mt-2 h-2" />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-yellow-500" />
              <span className="text-sm font-medium">負載平均</span>
            </div>
            <div className="text-2xl font-bold">{host.load_avg_1m.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              1m: {host.load_avg_1m.toFixed(2)} / 5m: {host.load_avg_5m.toFixed(2)} / 15m: {host.load_avg_15m.toFixed(2)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Server className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-medium">主機執行時間</span>
            </div>
            <div className="text-2xl font-bold">{formatUptime(host.uptime_seconds)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Process: {formatUptime(proc.uptime_seconds)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Queue & Process */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">爬取佇列狀態</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">並行 Workers</TableCell>
                  <TableCell className="text-right">
                    <span className="text-lg font-bold text-primary">{queue.active_workers}</span>
                    <span className="text-muted-foreground"> / {queue.max_workers}</span>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">待處理任務</TableCell>
                  <TableCell className="text-right font-bold">{queue.pending_tasks}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">處理中任務</TableCell>
                  <TableCell className="text-right font-bold">{queue.processing_tasks}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Worker 使用率</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Progress
                        value={queue.max_workers > 0 ? (queue.active_workers / queue.max_workers) * 100 : 0}
                        className="w-20 h-2"
                      />
                      <span className="text-sm font-medium">
                        {queue.max_workers > 0 ? Math.round((queue.active_workers / queue.max_workers) * 100) : 0}%
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Node.js 程序記憶體</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">PID</TableCell>
                  <TableCell className="text-right font-mono">{proc.pid}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">RSS（常駐記憶體）</TableCell>
                  <TableCell className="text-right font-bold">{proc.rss_mb} MB</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Heap Used</TableCell>
                  <TableCell className="text-right">{proc.heap_used_mb} MB</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium">Heap Total</TableCell>
                  <TableCell className="text-right">{proc.heap_total_mb} MB</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default function Logs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (['logs','monitor'] as const).includes(searchParams.get('tab') as 'logs') ? searchParams.get('tab') as string : 'logs';
  const setActiveTab = (t: string) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const { data: logs = [], isLoading, error } = useQuery<LogEntry[]>({
    queryKey: ["logs", sourceFilter, statusFilter],
    queryFn: async () => {
      const res = await logsApi.list({
        source: sourceFilter !== "all" ? sourceFilter : undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        limit: 500,
      });
      return res.data || [];
    },
    refetchInterval: 10000,
  });

  const filtered = logs.filter(log => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return log.message.toLowerCase().includes(q) ||
      log.task_id?.includes(searchQuery) ||
      log.action.toLowerCase().includes(q);
  });

  const sources = [...new Set(logs.map(l => l.source))];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">系統日誌</h1>
        <p className="text-muted-foreground">任務執行日誌與系統監控</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="logs">日誌紀錄</TabsTrigger>
          {isAdmin && <TabsTrigger value="monitor">系統監控</TabsTrigger>}
        </TabsList>

        <TabsContent value="logs" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="搜尋日誌..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9" />
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部來源</SelectItem>
                    {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部狀態</SelectItem>
                    <SelectItem value="success">成功</SelectItem>
                    <SelectItem value="error">錯誤</SelectItem>
                    <SelectItem value="info">資訊</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : error ? (
                <div className="text-center text-destructive py-8">載入失敗</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>時間</TableHead>
                      <TableHead>任務 ID</TableHead>
                      <TableHead>來源</TableHead>
                      <TableHead>操作</TableHead>
                      <TableHead>狀態</TableHead>
                      <TableHead>訊息</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">無日誌記錄</TableCell></TableRow>
                    ) : filtered.map(log => (
                      <TableRow key={log.id}>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{new Date(log.created_at).toLocaleString("zh-TW")}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.task_id ? <a href={`/tasks/${log.task_id}`} className="text-primary hover:underline">{log.task_id.substring(0, 8)}</a> : "—"}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{log.source}</Badge></TableCell>
                        <TableCell className="text-sm">{log.action}</TableCell>
                        <TableCell><Badge variant={levelVariant[log.status] || "default"}>{log.status}</Badge></TableCell>
                        <TableCell className="text-sm max-w-[300px] truncate" title={log.message}>{log.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="monitor" className="mt-4">
            <SystemMonitorTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
